import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Radio, RadioGroup, FormControlLabel, FormControl, Paper,
  Switch, InputAdornment, IconButton, Tooltip, Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { slugify } from '../../jobBoard/slugUtils';
import { getBoardUrl } from '../../jobBoard/hostSlug';

// URL pública del board para mostrar/copiar: en producción es el subdominio
// https://{slug}.amandahr.ai; en localhost/previews getBoardUrl devuelve la
// ruta relativa /empleos/:slug y se antepone el origin actual.
const boardDisplayUrl = (slug) => {
  const url = getBoardUrl(slug);
  return url.startsWith('http') ? url : `${window.location.origin}${url}`;
};

/**
 * Configuración de la Web de empleos (job board) — NIVEL ORGANIZACIÓN.
 * A diferencia de las otras tabs (por proyecto), esta config aplica a toda la
 * organización: el board lista las vacantes ATS activas de todos sus proyectos.
 */
const AtsJobBoardTab = () => {
  const { t } = useTranslation();
  const { organization, organizationName, authToken, showAlert } = useStore.getState();

  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState('');
  const [savedSlug, setSavedSlug] = useState(null);
  const [nameSource, setNameSource] = useState('organization');
  const [customName, setCustomName] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [slugCheck, setSlugCheck] = useState(null); // null | 'checking' | {available, message}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!organization) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get(`/organizations/ats-job-board`, {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = res.data.response?.data;
        if (data) {
          setEnabled(!!data.enabled);
          setSlug(data.slug || slugify(organizationName || ''));
          setSavedSlug(data.slug || null);
          setNameSource(data.nameSource || 'organization');
          setCustomName(data.customName || '');
          setBannerUrl(data.bannerUrl || '');
        }
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.jobBoard.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckSlug = async () => {
    const normalized = slugify(slug);
    setSlug(normalized);
    if (!normalized) return;
    try {
      setSlugCheck('checking');
      const res = await api.get(`/organizations/ats-job-board/check-slug`, {
        params: { organizationId: organization, slug: normalized },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSlugCheck(res.data.response || null);
    } catch (err) {
      console.error(err);
      setSlugCheck(null);
    }
  };

  const handleSave = async () => {
    const normalized = slugify(slug);
    if (enabled && !normalized) {
      showAlert(t('ats.config.jobBoard.slugRequired'), 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await api.put(
        `/organizations/ats-job-board`,
        { enabled, slug: normalized, nameSource, customName, bannerUrl: bannerUrl.trim() || null },
        {
          params: { organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        setSavedSlug(res.data.response.data?.slug || normalized);
        setSlug(res.data.response.data?.slug || normalized);
        setSlugCheck(null);
        showAlert(t('ats.config.jobBoard.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.jobBoard.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        showAlert(t('ats.config.jobBoard.slugTaken'), 'error');
      } else {
        showAlert(t('ats.config.jobBoard.saveError'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = savedSlug ? boardDisplayUrl(savedSlug) : null;

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      showAlert(t('ats.config.jobBoard.copied'), 'success');
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.jobBoard.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('ats.config.jobBoard.description')}
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('ats.config.jobBoard.orgLevelNote')}
      </Alert>
      {/* El slug es compartido con la "URL personalizada" del modo evaluaciones */}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('ats.config.jobBoard.sharedSlugNote')}
      </Alert>

      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label={t('ats.config.jobBoard.enabled')}
        sx={{ mb: 2, display: 'block' }}
      />

      {/* Slug */}
      <TextField
        fullWidth
        label={t('ats.config.jobBoard.slugLabel')}
        value={slug}
        onChange={(e) => { setSlug(e.target.value); setSlugCheck(null); }}
        onBlur={handleCheckSlug}
        helperText={
          slugCheck === 'checking'
            ? t('ats.config.jobBoard.checking')
            : slugCheck && !slugCheck.available
              ? t('ats.config.jobBoard.slugTaken')
              : t('ats.config.jobBoard.slugHint', { url: boardDisplayUrl(slugify(slug) || '...') })
        }
        error={!!(slugCheck && slugCheck !== 'checking' && !slugCheck.available)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {slugCheck === 'checking' && <CircularProgress size={16} />}
              {slugCheck && slugCheck !== 'checking' && slugCheck.available && <CheckIcon color="success" fontSize="small" />}
              {slugCheck && slugCheck !== 'checking' && !slugCheck.available && <CancelIcon color="error" fontSize="small" />}
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      {savedSlug && slugify(slug) !== savedSlug && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('ats.config.jobBoard.slugChangeWarning', { old: savedSlug })}
        </Alert>
      )}

      {/* Nombre mostrado en el header del board */}
      <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>
        {t('ats.config.jobBoard.nameTitle')}
      </Typography>
      <FormControl sx={{ mb: 1 }}>
        <RadioGroup value={nameSource} onChange={(e) => setNameSource(e.target.value)}>
          <FormControlLabel
            value="organization"
            control={<Radio />}
            label={t('ats.config.jobBoard.nameOrganization', { name: organizationName || '' })}
          />
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label={t('ats.config.jobBoard.nameCustom')}
          />
        </RadioGroup>
      </FormControl>
      {nameSource === 'custom' && (
        <TextField
          fullWidth
          label={t('ats.config.jobBoard.customName')}
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          sx={{ mb: 2 }}
        />
      )}

      {/* Banner opcional */}
      <TextField
        fullWidth
        label={t('ats.config.jobBoard.bannerUrl')}
        value={bannerUrl}
        onChange={(e) => setBannerUrl(e.target.value)}
        helperText={t('ats.config.jobBoard.bannerHint')}
        sx={{ mb: 2, mt: 1 }}
      />

      {/* Link público */}
      {enabled && publicUrl && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#f9f9f9', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
            {publicUrl}
          </Typography>
          <Tooltip title={t('ats.config.jobBoard.copy')}>
            <IconButton size="small" onClick={handleCopy}><CopyIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t('ats.config.jobBoard.open')}>
            <IconButton size="small" component="a" href={publicUrl} target="_blank" rel="noopener">
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.jobBoard.save')}
      </Button>
    </Box>
  );
};

export default AtsJobBoardTab;
